import React, { useRef, useEffect } from "react";

// Minimal login background: solid black + small glowing dots drifting left → right.
// Everything else (mesh, grid, chart, sport glyphs, corner glows) removed on purpose.
export const HitmaxHero = ({ children, className = "" }) => {
  const canvasRef = useRef(null);
  const raf = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    let W = 0, H = 0, dpr = 1, dots = [];

    const build = () => {
      const count = W > 640 ? 34 : 18;
      dots = [];
      for (let i = 0; i < count; i++) {
        dots.push({
          x: Math.random() * W,
          y: Math.random() * H,
          speed: 0.35 + Math.random() * 1.0,
          len: 10 + Math.random() * 30,
          size: Math.random() > 0.7 ? 1.9 : 1.2,
        });
      }
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = parent.getBoundingClientRect();
      W = rect.width; H = rect.height;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    const frame = () => {
      ctx.clearRect(0, 0, W, H);
      for (const s of dots) {
        s.x += s.speed;
        if (s.x - s.len > W) { s.x = -s.len; s.y = Math.random() * H; }
        const z = s.x / W;
        const col = z < 0.5 ? "0,230,118" : "212,175,55";
        const grad = ctx.createLinearGradient(s.x - s.len, s.y, s.x, s.y);
        grad.addColorStop(0, `rgba(${col},0)`);
        grad.addColorStop(1, `rgba(${col},0.45)`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.size;
        ctx.beginPath();
        ctx.moveTo(s.x - s.len, s.y);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, 6.29);
        ctx.fillStyle = `rgba(${col},0.9)`;
        ctx.fill();
      }
      raf.current = requestAnimationFrame(frame);
    };

    resize();
    frame();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className={`relative w-full h-full overflow-hidden bg-black ${className}`}>
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" data-testid="hero-canvas" />
      <div
        aria-hidden="true"
        data-testid="landing-top-ambiance"
        className="absolute inset-x-0 top-0 h-[46vh] pointer-events-none"
        style={{ background: "radial-gradient(130% 80% at 50% -12%, rgba(0,230,118,0.18) 0%, rgba(0,230,118,0.07) 34%, rgba(0,230,118,0) 68%)" }}
      />
      <div className="relative z-10 w-full h-full">{children}</div>
    </div>
  );
};
